import { useLanguage, Language } from "@/contexts/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import { publicRoutes } from "@/i18n/routes";

const languages: { code: Language; label: string; short: string }[] = [
  { code: "nl", label: "Nederlands", short: "NL" },
  { code: "en", label: "English", short: "EN" },
];

interface LanguageSwitcherProps {
  variant?: "ghost" | "outline";
  size?: "default" | "sm" | "icon";
  showLabel?: boolean;
  className?: string;
}

export default function LanguageSwitcher({
  variant = "ghost",
  size = "sm",
  showLabel = false,
  className,
}: LanguageSwitcherProps) {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const current = languages.find((l) => l.code === language) || languages[0];

  const handleSwitch = (targetLang: Language) => {
    if (targetLang === language) return;

    const path = location.pathname;
    const isDashboard = path.startsWith("/dashboard") || path.startsWith("/admin");

    if (isDashboard) {
      // Just switch language, don't change URL
      setLanguage(targetLang);
      return;
    }

    // Find current route and navigate to translated version
    const isEn = path.startsWith("/en/") || path === "/en";
    const currentLang: Language = isEn ? "en" : "nl";
    const cleanPath = isEn ? (path.slice(3) || "/") : path;

    const route = publicRoutes.find((r) => r[currentLang] === cleanPath);

    if (route) {
      const targetPath = targetLang === "en" ? `/en${route.en}` : route.nl;
      setLanguage(targetLang);
      navigate(targetPath);
    } else {
      // Fallback: just add/remove prefix
      const targetPath = targetLang === "en" ? `/en${cleanPath}` : cleanPath;
      setLanguage(targetLang);
      navigate(targetPath);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className={`gap-1.5 ${className ?? ""}`} aria-label={`Taal: ${current.label}`}>
          <Globe className="w-4 h-4 shrink-0" />
          {showLabel ? (
            <span className="text-sm">{current.label}</span>
          ) : (
            <span className="text-xs font-semibold">{current.short}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSwitch(lang.code)}
            className={language === lang.code ? "bg-secondary font-medium" : ""}
          >
            <span className="mr-2 text-xs font-semibold w-5">{lang.short}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
